import { Box, Chip, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type JSX, memo, type ReactNode, useCallback, useMemo } from "react";
import { ALPHA, BADGE, CONTROL_HEIGHT, DISABLED_CONTROL, FILTER_CHIP, FONT_SIZE, INK, INK_BLACK, MOTION, NEUTRAL, STATUS_BADGE, SURFACE } from "@/theme/tokens";
import { TOUCH_TARGET_SX } from "@/ui/touchTarget";

/**
 * - `filled` / `outlined` / `filled-no-border`: the compact 28px chips.
 * - `outlined-brand`: a toolbar-height (`CONTROL_HEIGHT.STANDARD`) filter pill — white with a hairline at rest,
 *   brand edge + wash + brand ink when selected — for a single-choice filter row that sits beside a search field
 *   and the Sort / Columns triggers. Draws `icon` inline in its own colour (MUI's icon slot repaints it grey) and
 *   exposes the pressed state as `aria-pressed`.
 */
type ChipVariant = "filled" | "outlined" | "filled-no-border" | "outlined-brand";

export type SystemChipItem<T> = {
	value: T;
	label: string;
	startAdornment?: JSX.Element;
	icon?: JSX.Element;
	/** Mark after the label that shows a state, like a tick. It sits inside the label to share its color. */
	endAdornment?: JSX.Element;
	disabled?: boolean;
	/**
	 * Trailing count badge ("6"). `0` IS drawn — on a filter row, "this option has none" is information, and the
	 * caller usually pairs it with `disabled`. Omit for no badge.
	 */
	count?: number;
	/**
	 * Accessible name override, when the visible label + count don't say it on their own ("7:00 AM–3:00 PM, 6
	 * scheduled"). Omit to let the visible text name the chip.
	 */
	ariaLabel?: string;
	/**
	 * A short, already-translated note after the count ("2 picked"), led by a small brand dot — for state the
	 * chip holds that the view doesn't currently show (picks hidden behind another filter). Omit for none. Fold it
	 * into `ariaLabel` too when you override the accessible name.
	 */
	note?: string;
	/** Crosses out the label text only, for an option someone else turned off. Screen readers don't announce it, so say why in text nearby. */
	struck?: boolean;
};

const STRUCK_LABEL_SX = { textDecoration: "line-through" } as const;

type MemoizedChipProps<T> = {
	item: SystemChipItem<T>;
	selectedItems: SystemChipItem<T>[];
	selectedSx?: SxProps<Theme>;
	unselectedSx?: SxProps<Theme>;
	onChange: (value: SystemChipItem<T>, selected: boolean) => void;
	maxSelectedAmount?: number;
	variant: ChipVariant;
	touchTarget?: boolean;
	announcePressed?: boolean;
	dataTestId?: string;
};

const isFilledVariant = (variant: ChipVariant): boolean => variant === "filled" || variant === "filled-no-border";

/**
 * The count badge's fill for the chip's state. On a solid brand chip (a selected `filled` one) it inverts to white,
 * since a brand badge would vanish into the fill; everywhere else it is a brand tint at rest and solid brand once
 * the chip is picked, so the live choice's number reads first.
 */
const countBadgeToneSx = (variant: ChipVariant, isSelected: boolean, disabled: boolean) => (theme: Theme) => {
	if (disabled) return { backgroundColor: DISABLED_CONTROL.FILL, color: DISABLED_CONTROL.INK };
	if (isSelected && isFilledVariant(variant)) return { backgroundColor: SURFACE, color: theme.palette.primary.main };
	if (isSelected) return { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText };
	return { backgroundColor: alpha(theme.palette.primary.main, FILTER_CHIP.COUNT_TINT_ALPHA), color: theme.palette.primary.dark };
};

const ChipCountBadge = ({
	count,
	variant,
	isSelected,
	disabled,
	dataTestId,
}: {
	count: number;
	variant: ChipVariant;
	isSelected: boolean;
	disabled: boolean;
	dataTestId?: string;
}) => (
	<Box
		component="span"
		data-testid={dataTestId}
		sx={[
			{
				minWidth: BADGE.PILL_SIZE,
				height: BADGE.PILL_SIZE,
				px: 0.75,
				boxSizing: "border-box",
				borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				flex: "none",
				fontSize: FONT_SIZE.LABEL_XS,
				fontWeight: 700,
				lineHeight: 1,
				fontVariantNumeric: "tabular-nums",
				transition: `background-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			},
			countBadgeToneSx(variant, isSelected, disabled),
		]}
	>
		{count}
	</Box>
);

/** "• 2 picked" — the item's `note`: brand dot + brand caption, quieter than the label and the count. */
const ChipNote = ({ note, dataTestId }: { note: string; dataTestId?: string }) => (
	<Box
		component="span"
		data-testid={dataTestId}
		sx={(theme) => ({
			display: "inline-flex",
			alignItems: "center",
			gap: 0.5,
			flex: "none",
			fontSize: FONT_SIZE.CAPTION,
			fontWeight: 600,
			color: theme.palette.primary.main,
			whiteSpace: "nowrap",
		})}
	>
		<Box
			component="span"
			aria-hidden
			sx={(theme) => ({
				width: FILTER_CHIP.NOTE_DOT_SIZE,
				height: FILTER_CHIP.NOTE_DOT_SIZE,
				borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
				backgroundColor: theme.palette.primary.main,
				flex: "none",
			})}
		/>
		{note}
	</Box>
);

/**
 * The chip's label node. A plain string for the historical chips (so their DOM is unchanged); a `struck` item
 * wraps its text in a span. Composed only when there is something to compose — a count badge, a note, or the
 * `outlined-brand` variant's inline icon.
 */
const buildChipLabel = <T,>(item: SystemChipItem<T>, variant: ChipVariant, isSelected: boolean, itemTestId?: string): ReactNode => {
	const inlineIcon = variant === "outlined-brand" ? item.icon : undefined;
	if (item.count === undefined && item.note === undefined && !inlineIcon) {
		const labelText = item.struck ? (
			<Box component="span" sx={STRUCK_LABEL_SX}>
				{item.label}
			</Box>
		) : (
			item.label
		);
		if (!item.endAdornment) return labelText;
		return (
			<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
				{labelText}
				{item.endAdornment}
			</Box>
		);
	}
	return (
		<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, minWidth: 0 }}>
			{inlineIcon && (
				<Box component="span" sx={{ display: "inline-flex", flex: "none", fontSize: FILTER_CHIP.ICON_SIZE }}>
					{inlineIcon}
				</Box>
			)}
			<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(item.struck && STRUCK_LABEL_SX) }}>
				{item.label}
			</Box>
			{item.count !== undefined && (
				<ChipCountBadge
					count={item.count}
					variant={variant}
					isSelected={isSelected}
					disabled={!!item.disabled}
					dataTestId={itemTestId ? `${itemTestId}-count` : undefined}
				/>
			)}
			{item.note !== undefined && <ChipNote note={item.note} dataTestId={itemTestId ? `${itemTestId}-note` : undefined} />}
			{item.endAdornment}
		</Box>
	);
};

/** The `outlined-brand` pill's look. The three historical variants keep their own sx, untouched, below. */
const outlinedBrandSx = (isSelected: boolean) => (theme: Theme) => {
	const brand = theme.palette.primary.main;
	const restingBg = isSelected ? alpha(brand, FILTER_CHIP.SELECTED_WASH_ALPHA) : SURFACE;
	return {
		"&.MuiChip-root": {
			height: CONTROL_HEIGHT.STANDARD,
			borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
			border: "1px solid",
			borderColor: isSelected ? brand : NEUTRAL[400],
			backgroundColor: restingBg,
			color: isSelected ? theme.palette.primary.dark : INK.SECONDARY,
			fontSize: FONT_SIZE.EMPHASIS,
			fontWeight: 500,
			transition: [
				`background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				`border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
				`color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
				`transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
			].join(", "),
			"@media (prefers-reduced-motion: reduce)": { transition: "none" },
		},
		"& .MuiChip-label": { px: 1.75, display: "inline-flex", alignItems: "center" },
		// Hover is a pointer affordance only — a tap on a touch screen must not leave a stuck wash behind.
		"@media (hover: hover) and (pointer: fine)": {
			"&.MuiChip-clickable:hover": {
				backgroundColor: isSelected ? alpha(brand, FILTER_CHIP.SELECTED_HOVER_WASH_ALPHA) : NEUTRAL[50],
				borderColor: isSelected ? brand : NEUTRAL[450],
			},
		},
		// MUI lifts a pressed clickable chip with a shadow; this one presses in instead, like the app's buttons.
		"&.MuiChip-clickable:active": {
			boxShadow: "none",
			transform: `scale(${FILTER_CHIP.PRESS_SCALE})`,
			"@media (prefers-reduced-motion: reduce)": { transform: "none" },
		},
		"&.Mui-focusVisible": {
			backgroundColor: restingBg,
			boxShadow: `0 0 0 ${FILTER_CHIP.FOCUS_RING_PX}px ${alpha(brand, ALPHA.FOCUS_RING)}`,
		},
		// Quiet, not faded: MUI's disabled opacity would also wash out the count, which is the reason it's disabled.
		"&.Mui-disabled": { opacity: 1, color: DISABLED_CONTROL.INK, borderColor: DISABLED_CONTROL.BORDER, backgroundColor: SURFACE },
	};
};

const ChipComponent = <T,>(props: MemoizedChipProps<T>) => {
	const { item, onChange, selectedSx, selectedItems, unselectedSx, maxSelectedAmount, variant, touchTarget, announcePressed, dataTestId } = props;
	const isSelected = useMemo(() => selectedItems.some((value) => value.value === item.value), [selectedItems, item]);
	const handleChange = useCallback(() => {
		if (maxSelectedAmount && !isSelected && selectedItems.length >= maxSelectedAmount) {
			return; // Prevent selection if max selected amount is reached
		}
		onChange(item, !isSelected);
	}, [isSelected, onChange, item, maxSelectedAmount, selectedItems.length]);

	const isBrand = variant === "outlined-brand";
	const label = buildChipLabel(item, variant, isSelected, dataTestId);
	const stateSx = isSelected ? selectedSx : unselectedSx;

	return (
		<Chip
			data-testid={dataTestId}
			disabled={item.disabled}
			avatar={item.startAdornment}
			// `outlined-brand` draws the icon inside its label instead (see `buildChipLabel`).
			icon={isBrand ? undefined : item.icon}
			aria-label={item.ariaLabel}
			aria-pressed={isBrand || announcePressed ? isSelected : undefined}
			key={item.label}
			sx={
				isBrand
					? // A new variant, so it can take the sx ARRAY (deep-merged). The historical chips keep the flat spread
						// below — callers pass plain objects that replace whole keys there, and that must not change.
						[outlinedBrandSx(isSelected), touchTarget ? TOUCH_TARGET_SX : {}, ...(Array.isArray(stateSx) ? stateSx : [stateSx])]
					: {
							"&.MuiChip-root:hover": {
								backgroundColor: isFilledVariant(variant) && isSelected ? STATUS_BADGE.BRAND_FALLBACK : "default.main",
								color: isFilledVariant(variant) && isSelected ? SURFACE : INK.SECONDARY,
							},
							"&.MuiChip-root": {
								borderColor: variant === "outlined" ? (isSelected ? INK.SECONDARY : "transparent") : undefined,
								backgroundColor: variant === "outlined" && !isSelected ? alpha(INK_BLACK, 0.08) : "default.main",
								color: isFilledVariant(variant) && isSelected ? SURFACE : INK.SECONDARY,
								border: variant === "filled-no-border" ? "none" : undefined,
							},
							height: "28px",
							...(touchTarget ? TOUCH_TARGET_SX : {}),
							...stateSx,
						}
			}
			label={label}
			onClick={handleChange}
			variant={isSelected && isFilledVariant(variant) ? "filled" : "outlined"}
			color={isSelected && isFilledVariant(variant) ? "primary" : "default"}
		/>
	);
};

const MemoizedChip = memo(ChipComponent) as <T>(props: MemoizedChipProps<T>) => JSX.Element;

type Props<T> = {
	variant?: ChipVariant;
	items?: SystemChipItem<T>[];
	selectedSx?: SxProps<Theme>;
	unselectedSx?: SxProps<Theme>;
	onChange: (value: SystemChipItem<T>, selected: boolean) => void;
	selectedItems: SystemChipItem<T>[];
	rootSx?: SxProps<Theme>;
	maxSelectedAmount?: number;
	forceFitContent?: boolean;
	/** Grow each chip's hit area to `TOUCH_TARGET_MIN` on coarse pointers (a chip a finger taps to answer). */
	touchTarget?: boolean;
	/** Announce chips as on/off toggles for screen readers, for chips that switch something rather than filter. */
	announcePressed?: boolean;
	dataTestId?: string;
	/** Per-chip test id (list items need per-item ids for E2E targeting). */
	getItemDataTestId?: (item: SystemChipItem<T>) => string;
};

/**
 * SystemChips component renders a set of chips that can be selected or unselected.
 * @param items - Array of items to be displayed as chips, each with a value and label.
 * @param rootSx
 * @param selectedItems - Array of currently selected items.
 * @param selectedSx
 * @param unselectedSx
 * @param onChange - Callback function to handle selection changes, receives the item and its selected state.
 * @param maxSelectedAmount - Optional maximum number of chips that can be selected at once.
 * @param variant
 * @param forceFitContent - If true, the width of the component will be set to fit its content, otherwise it will take full width.
 */
export const SystemChips = <T,>({
	items,
	rootSx,
	selectedItems,
	selectedSx,
	unselectedSx,
	onChange,
	maxSelectedAmount,
	variant = "filled",
	forceFitContent,
	touchTarget,
	announcePressed,
	dataTestId,
	getItemDataTestId,
}: Props<T>) => {
	return (
		<Box
			id={"system-chips"}
			data-testid={dataTestId}
			key={"system-tabs"}
			sx={{
				width: forceFitContent ? "fit-content" : "100%",
				height: "fit-content",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Box
				sx={[
					{
						width: "fit-content",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexWrap: "wrap",
						gap: 1,
						position: "relative",
					},
					...(Array.isArray(rootSx) ? rootSx : [rootSx]),
				]}
			>
				{items?.map((item) => {
					return (
						<MemoizedChip
							key={item.label}
							item={item}
							selectedItems={selectedItems}
							selectedSx={selectedSx}
							unselectedSx={unselectedSx}
							onChange={onChange}
							maxSelectedAmount={maxSelectedAmount}
							variant={variant}
							touchTarget={touchTarget}
							announcePressed={announcePressed}
							dataTestId={getItemDataTestId?.(item)}
						/>
					);
				})}
			</Box>
		</Box>
	);
};
